import React, { useState } from 'react';
import { Phone, TrendingUp, CheckCircle, MapPin, Users, Award, Shield, Mail, MessageCircle } from 'lucide-react';
import { createContactMessage } from '../services/api';
import { useToast } from '../hooks/use-toast';

const FranchisePage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Franchise Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await createContactMessage(formData);
      toast({
        title: "Success!",
        description: "Your franchise inquiry has been submitted. We'll contact you soon.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'Franchise Inquiry',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit inquiry. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <div className="relative h-screen overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_extreme-homepage/artifacts/miyamfwr_MAP.jpg"
          alt="Franchise Opportunities"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/56 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black text-white uppercase tracking-tight mb-4 sm:mb-6 leading-tight">
              Franchise<br />Opportunities
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-6 sm:mb-8 max-w-2xl">
              Call for franchise opportunities
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:8700334154"
                className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-bold text-xl transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
              >
                <Phone size={24} />
                <span>Call Now</span>
              </a>
              <a
                href="https://wa.me/918700334154"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-green-500 hover:bg-green-400 text-white px-8 py-4 rounded-full font-bold text-xl transform hover:scale-105 transition-all duration-300 shadow-lg shadow-green-500/50"
              >
                <MessageCircle size={24} />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Partner Banner */}
      <div className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_extreme-homepage/artifacts/aj5cf68g_background.jpg"
                alt="Partner with us"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/56 via-black/28 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <h3 className="text-6xl font-black text-white mb-2">PARTNER</h3>
                  <p className="text-2xl text-yellow-400 font-bold">WITH US</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
                Leap Into the Future of <span className="text-yellow-400">Extreme Sports</span>
              </h2>
              <p className="text-gray-300 leading-relaxed">
                At <strong className="text-white">Indyabungy</strong>, we believe in pushing limits, embracing adrenaline, and creating unforgettable experiences that challenge the ordinary. Together with our partners, we aim to build a culture of adventure, safety, and bold innovation in the world of extreme sports.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Your partnership with <strong className="text-white">Indyabungy</strong> is more than just business, it is a shared commitment to redefining thrill seeking in India. By collaborating, we can bring world class bungee jumping experiences to more people, raise safety standards, and grow a vibrant community of adventurers.
              </p>
              <div className="pt-4">
                <p className="text-2xl font-bold text-yellow-400">Let us Do Bungee Together.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-4 uppercase">
            Ready to Turn Your Ambition<br />Into a Thriving Reality?
          </h2>
          <div className="w-32 h-1 bg-yellow-400 mx-auto mb-16"></div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Impressive ROI */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300 transform hover:scale-105">
              <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="text-yellow-400" size={40} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4 uppercase">Impressive ROI</h3>
              <p className="text-gray-300 leading-relaxed">
                With an impressive ROI, our partnership promises to take your business to new heights.
              </p>
            </div>

            {/* Proven Business Model */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300 transform hover:scale-105">
              <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-yellow-400" size={40} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4 uppercase">Proven Business Model</h3>
              <p className="text-gray-300 leading-relaxed">
                We've aced the strategy, so you can hit the ground running.
              </p>
            </div>

            {/* Certified Professionals */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300 transform hover:scale-105">
              <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
                <Award className="text-yellow-400" size={40} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4 uppercase">Certified Professionals</h3>
              <p className="text-gray-300 leading-relaxed">
                Work with certified professionals you can trust for expertise and results.
              </p>
            </div>

            {/* Exclusive Territory */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300 transform hover:scale-105">
              <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
                <MapPin className="text-yellow-400" size={40} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4 uppercase">Exclusive Territory</h3>
              <p className="text-gray-300 leading-relaxed">
                Secure your exclusive territory in the exciting world of adventure sports.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Founder Message */}
      <div className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-16 uppercase">
            What Does the <span className="text-yellow-400">Bungyman</span> Say?
          </h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_extreme-homepage/artifacts/m8dw37sa_sid%20picture.png"
                alt="Siddharth Pareek - Founder"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6">
              <p className="text-gray-300 leading-relaxed text-lg">
                I am <strong className="text-white">Siddharth Pareek</strong>, Co-founder of Indya Bungy, which I founded on 3rd August 2019 along with my partners Dheeresh Pareek and Ratish Nair. Our journey began with a vision to bring the thrilling experience of bungee jumping to Goa, but the overwhelming response and love we received from visitors across India inspired us to expand beyond our initial plan.
              </p>
              <p className="text-gray-300 leading-relaxed text-lg">
                Recognizing the growing demand and excitement for this globally loved adventure sport, we decided to offer franchising opportunities, making it possible for people to enjoy the Indya Bungy experience in their own cities or nearby travel destinations without having to journey all the way to Goa.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-black text-yellow-400 mb-1">10</div>
                  <div className="text-gray-400 text-sm">Years Experience</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-black text-yellow-400 mb-1">50K</div>
                  <div className="text-gray-400 text-sm">Safe Jumps</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Partner With Us */}
      <div className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-16">
            Why Partner With <span className="text-yellow-400">Indya Bungy?</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-900/10 border border-yellow-400/20 rounded-2xl p-8">
              <Shield className="text-yellow-400 mb-4" size={40} />
              <h3 className="text-xl font-bold text-white mb-3">Brand Recognition</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Leverage India's most trusted bungee jumping brand with 10 years of excellence and nationwide recognition.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-900/10 border border-yellow-400/20 rounded-2xl p-8">
              <Users className="text-yellow-400 mb-4" size={40} />
              <h3 className="text-xl font-bold text-white mb-3">Comprehensive Training</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Get complete training for your team, from safety protocols to customer service and operations management.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-900/10 border border-yellow-400/20 rounded-2xl p-8">
              <TrendingUp className="text-yellow-400 mb-4" size={40} />
              <h3 className="text-xl font-bold text-white mb-3">Marketing Support</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Benefit from our established marketing channels, social media presence, and promotional campaigns.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-4 uppercase">
            Contact Us
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Fill out the form below and we will get back to you within 1 Day
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="text"
                name="name"
                placeholder="Full name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
              />
              <input
                type="email"
                name="email"
                placeholder="Email address"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="tel"
                name="phone"
                placeholder="Phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
              />
              <input
                type="text"
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
              />
            </div>

            <textarea
              name="message"
              placeholder="Write a message"
              rows="6"
              value={formData.message}
              onChange={handleChange}
              className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors resize-none"
            ></textarea>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-yellow-400 hover:bg-yellow-300 text-black px-12 py-4 rounded-full font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
              </button>
            </div>
          </form>

          {/* Alternative Contact */}
          <div className="mt-16 text-center">
            <p className="text-gray-400 mb-6">Or reach out to us directly:</p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <a
                href="tel:8700334154"
                className="inline-flex items-center space-x-2 text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                <Phone size={20} />
                <span className="font-bold">Call Now</span>
              </a>
              <a
                href="mailto:crew.indyabungy@gmail.com"
                className="inline-flex items-center space-x-2 text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                <Mail size={20} />
                <span className="font-bold">Email Us</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchisePage;
